import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Toast, type ToastMessage } from 'primereact/toast'
import { registerToast } from '../../api/toastBridge'

/**
 * Global Toast sağlayıcısı (paylaşılan ui katmanı — Reuse Mandate). Tek bir `<Toast>` kök seviyede
 * render edilir; ekranlar `useToast()` ile bildirim gösterir (her sayfada ayrı `useRef<Toast>` + `<Toast>`
 * tekrarı kaldırılır). Süreler anayasa Page Standards: başarı ~3sn, hata ~4sn.
 */
export interface ToastApi {
  show: (message: ToastMessage | ToastMessage[]) => void
  success: (summary: string, detail?: string) => void
  error: (summary: string, detail?: string) => void
  /**
   * **Kısmî başarı** — işlem yürüdü ama sonucu kullanıcının bilmesi gereken bir kısıtla sınırlandı
   * (ör. toplu hatırlatma üst sınıra takıldı, hiç bildirim üretilmedi). Hata değildir: `error` demek
   * kullanıcıyı gereksiz yere geri adım atmaya iterdi; `success` demek ise eksiği gizlerdi.
   * Süresi hatayla aynıdır (~4sn) — okunması gereken bir metin taşır.
   */
  warn: (summary: string, detail?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: Readonly<{ children: ReactNode }>) {
  const ref = useRef<Toast>(null)
  const api = useMemo<ToastApi>(
    () => ({
      show: (m) => ref.current?.show(m),
      success: (summary, detail) =>
        ref.current?.show({ severity: 'success', summary, detail, life: 3000 }),
      error: (summary, detail) =>
        ref.current?.show({ severity: 'error', summary, detail, life: 4000 }),
      warn: (summary, detail) =>
        ref.current?.show({ severity: 'warn', summary, detail, life: 4000 }),
    }),
    [],
  )
  // React-dışı katmanlar (axios interceptor, RQ global onError) bu API'yi köprü üzerinden kullanır.
  useEffect(() => {
    registerToast(api)
    return () => registerToast(null)
  }, [api])
  return (
    <ToastContext.Provider value={api}>
      <Toast ref={ref} />
      {children}
    </ToastContext.Provider>
  )
}

/** Kök `ToastProvider` içinden bildirim API'si. Sağlayıcı dışında kullanım hata verir. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast, <ToastProvider> içinde kullanılmalıdır')
  return ctx
}
